/**
 * PaperListControls — 시험지 목록의 범위·검색·정렬 표시 부품.
 *
 * API·페이지·선택 상태는 갖지 않는다. 시험지 관리 화면과 학생 허브 선택창이 같은 모양을
 * 쓰되 서로 다른 조회 계약을 지킬 수 있도록 값과 변경 콜백만 받는다.
 */
import React, { useRef } from 'react';
import { Search, User, Users, X } from 'lucide-react';

// 시험지 목록이 쓰는 기본 라벨. 같은 「전체 / 내 것」 가름을 다른 말로 부르는 화면이 있어
// 목록을 인자로도 받되 기본값을 이 상수로 둔다 — 기존 호출부는 아무것도 넘기지 않아도 그대로다.
// 「내 것」이라고만 하지 않는다 — 같은 화면 왼쪽에 폴더 범위 스위치가 나란히 서고 그쪽은 「내 폴더」라,
// 대상을 말하지 않는 쪽은 무엇의 범위인지 다시 찾게 된다.
const SCOPE_OPTIONS = [
  { mine: false, label: '전체', Icon: Users },
  { mine: true, label: '내 시험지', Icon: User },
];

// 무리 이름(groupLabel)도 인자로 연다. 한 화면에 이 스위치가 둘 서면(폴더 트리 · 시험지 목록)
// 눈으로는 자리와 라벨로 갈리지만 스크린리더는 무리 이름만 읽어, 이름이 같으면 어느 쪽인지 구별되지 않는다.
// 기본값이 「범위」가 아니라 「시험지 범위」인 까닭: 위 기본 라벨이 이미 시험지의 말이라(「내 시험지」)
// 무리 이름만 대상을 안 말하면 글자와 소리가 짝이 어긋난다. 폴더 트리는 제 이름을 따로 넘긴다.
export function PaperScopeControls({ mine, onMineChange, countText = '', style, options = SCOPE_OPTIONS, groupLabel = '시험지 범위' }) {
  return (
    <div className="flex items-center gap-2" style={style}>
      <div className="flex min-w-0 gap-1 overflow-x-auto" role="group" aria-label={groupLabel}>
        {options.map(({ mine: value, label, Icon }) => (
          <button
            key={String(value)}
            type="button"
            onClick={() => onMineChange(value)}
            aria-pressed={mine === value}
            className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-2xl px-3 py-1.5 text-xs transition-colors ${
              mine === value ? 'bg-primary font-bold text-white' : 'font-medium text-gray-600 hover:bg-primary hover:text-white'
            }`}
          >
            <Icon aria-hidden="true" className="size-3.5" />
            {label}
          </button>
        ))}
      </div>
      {/* 셀 것이 없는 호출에서도 이 칸은 지운다고 이득이 없다 — 비어 있으면 글줄이 안 생겨 높이가 0이라
          버튼 줄의 겉모습이 그대로고, aria-live 영역은 미리 서 있어야 나중에 채워지는 값을 읽어 준다. */}
      <span role="status" aria-live="polite" className="min-w-0 flex-1 truncate text-right text-[11px] text-gray-400">
        {countText}
      </span>
    </div>
  );
}

export function PaperSearchSortControls({
  query,
  onQueryChange,
  sortKey,
  onSortChange,
  sortOptions = [],
  searchLabel = '시험지 제목으로 검색',
  placeholder = '제목으로 검색',
  style,
}) {
  const searchRef = useRef(null);

  return (
    <div className="flex items-center gap-1.5" style={style}>
      <div className="relative min-w-0 flex-1">
        <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-300" />
        <input
          ref={searchRef}
          type="text"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          aria-label={searchLabel}
          placeholder={placeholder}
          className="w-full rounded-lg border px-8 py-1.5 text-xs focus:border-primary focus:outline-none"
        />
        {query !== '' && (
          <button
            type="button"
            onClick={() => { onQueryChange(''); searchRef.current?.focus(); }}
            aria-label="검색어 지우기"
            title="지우기"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X aria-hidden="true" className="size-3.5" />
          </button>
        )}
      </div>
      <select
        value={sortKey}
        onChange={(event) => onSortChange(event.target.value)}
        aria-label="정렬 순서"
        className="shrink-0 rounded-lg border px-2 py-1.5 text-xs focus:border-primary focus:outline-none"
      >
        {sortOptions.map((option) => {
          const value = option.value ?? option.key;
          return <option key={value} value={value}>{option.label}</option>;
        })}
      </select>
    </div>
  );
}
